import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { Task, TaskStatus } from '../types';

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (id) loadTask(parseInt(id, 10));
  }, [id]);

  const loadTask = async (taskId: number) => {
    try {
      setLoading(true);
      const data = await api.getTask(taskId);
      setTask(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!task) return;

    try {
      await api.updateTaskStatus(task.id, newStatus);
      loadTask(task.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !comment.trim()) return;

    try {
      setSubmittingComment(true);
      await api.addTaskComment(task.id, comment.trim());
      setComment('');
      loadTask(task.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="error-message">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/tasks')}>
          Назад к списку
        </button>
      </div>
    );
  }

  if (!task) {
    return (
      <div>
        <p>Задача не найдена</p>
        <button className="btn btn-secondary" onClick={() => navigate('/tasks')}>
          Назад к списку
        </button>
      </div>
    );
  }

  const canUpdate = user?.role === 'admin' || user?.role === 'manager' || task.assigned_user_id === user?.id;

  return (
    <div className="task-detail">
      <button className="btn btn-secondary" onClick={() => navigate('/tasks')} style={{ marginBottom: '1rem' }}>
        ← Назад
      </button>

      <div className="task-detail-header">
        <h1 className="task-detail-title">{task.title}</h1>
        <div className="task-detail-meta">
          <span className={`badge badge-${task.status === 'completed' ? 'completed' : task.status === 'in_progress' ? 'manager' : 'employee'}`}>
            {getStatusText(task.status)}
          </span>
          {task.due_date && (
            <span>📅 {new Date(task.due_date).toLocaleString('ru-RU')}</span>
          )}
          {task.establishment_name && <span>🏢 {task.establishment_name}</span>}
        </div>
      </div>

      {task.description && (
        <div className="card">
          <h3 style={{ marginBottom: '0.5rem' }}>Описание</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{task.description}</p>
        </div>
      )}

      {/* Status Actions */}
      {canUpdate && !['completed', 'cancelled'].includes(task.status) && (
        <div className="task-actions">
          <button className="btn btn-primary" onClick={() => handleStatusChange('in_progress')}>
            В работу
          </button>
          <button className="btn btn-secondary" onClick={() => handleStatusChange('questions')}>
            Есть вопросы
          </button>
          <button className="btn btn-success" onClick={() => handleStatusChange('completed')}>
            Выполнено
          </button>
          <button className="btn btn-danger" onClick={() => handleStatusChange('cancelled')}>
            Отмена
          </button>
        </div>
      )}

      {/* Comments */}
      <div className="comments-section">
        <h3 style={{ marginBottom: '1rem' }}>Комментарии</h3>

        {task.comments && task.comments.length > 0 ? (
          <div>
            {task.comments.map((c) => (
              <div key={c.id} className="comment">
                <div className="comment-header">
                  <span className="comment-author">{c.author_name || 'Пользователь'}</span>
                  <span className="comment-date">
                    {new Date(c.created_at).toLocaleString('ru-RU')}
                  </span>
                </div>
                <p className="comment-text">{c.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>Нет комментариев</p>
        )}

        <form onSubmit={handleAddComment}>
          <div className="form-group">
            <textarea
              className="form-textarea"
              placeholder="Добавить комментарий..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submittingComment || !comment.trim()}>
            {submittingComment ? 'Отправка...' : 'Отправить'}
          </button>
        </form>
      </div>
    </div>
  );
}

function getStatusText(status: string): string {
  const map: Record<string, string> = {
    pending: 'Ожидает',
    in_progress: 'В работе',
    questions: 'Вопросы',
    completed: 'Выполнено',
    cancelled: 'Отменено',
  };
  return map[status] || status;
}
